/**
 * Синхронизация состояния синхронного аудио через data channel LiveKit.
 *
 * Звук (микрофон + музыка/метроном, смикшированные в один трек) идёт по RTP,
 * а сюда передаётся СОСТОЯНИЕ для визуала и UI слушателя:
 *  - метроном: bpm, долей в такте, wallStart — слушатель рисует доли в такт ведущему;
 *  - плеер: имя файла и позиция;
 *  - wallStart — старт по wall-clock (+ опциональная поправка часов, см. ниже);
 *  - scheduleDelayMs — запас планирования старта (подсказка для начальной
 *    оценки задержки звука у слушателя).
 *
 * Служебные сообщения ping/pong — синхронизация часов машин (NTP-lite):
 * пингер шлёт { t0 }, резондер отвечает { t0, t1, t2 }.
 *
 * Heartbeat: пока играет музыка/метроном, последнее start-сообщение
 * переотправляется раз в ~5 с — поздние подключения и потерянные
 * сообщения при reconnect получают актуальное состояние.
 */
export const JUKEBOX_SYNC_TOPIC = 'jukebox-sync';

/** Heartbeat: переотправка start-сообщения, пока играет (мс). */
export const SYNC_HEARTBEAT_MS = 5000;

/**
 * Рассчитывает смещение часов резондента относительно пингера (NTP-формула):
 * θ = ((t1 − t0) + (t2 − t3)) / 2, где
 *  t0 — отправка пинга (часы пингера), t1 — приём (часы резондента),
 *  t2 — отправка понга (часы резондента), t3 — приём (часы пингера).
 * Результат: «часы резондента = часы пингера + θ».
 * Возвращает также RTT = (t3 − t0) − (t2 − t1), мс.
 */
export const computeClockOffsetMs = ({ t0, t1, t2, t3 }) => {
  const offsetMs = ((t1 - t0) + (t2 - t3)) / 2;
  const rttMs = (t3 - t0) - (t2 - t1);
  return { offsetMs, rttMs };
};

/**
 * Адаптивный запас планирования старта звука: сообщение о старте должно
 * дойти до слушателя раньше звука. От RTT сети (макс по известным участникам).
 */
export const computeScheduleDelayMs = (maxRttMs) =>
  Math.min(2000, Math.max(250, Math.round(maxRttMs / 2) + 100));

/**
 * Свежесть start-сообщения: heartbeat от ведущего держит его актуальным,
 * пропал — считаем, что трансляция завершилась (а stop не дошёл).
 */
export const isSyncFresh = (raw, nowMs, staleMs = 20000) =>
  raw.action !== 'start' || nowMs - raw.receivedAt <= staleMs;

/**
 * Расшифровывает payload data-сообщения. Возвращает null, если сообщение
 * не относится к синхронному аудио или не распарсилось.
 */
export const decodeSyncMessage = (payload) => {
  try {
    const data = JSON.parse(new TextDecoder().decode(payload));
    if (data && typeof data === 'object' && data.type === JUKEBOX_SYNC_TOPIC) {
      return data;
    }
  } catch {
    // не наш формат — игнорируем
  }
  return null;
};
